import React, { useEffect, useState } from "react";
import { Box, Text, Button, Alert, AlertIcon } from "@chakra-ui/react";
import dayjs from "@tether-web-portal/dayjs-setup";
import { DeviceWithLastSample } from "@tether-web-portal/types/DeviceWithLastSample";
import { PropertyDetail } from "@tether-web-portal/types/PropertyDetail";
import { formatDeviceName } from "@tether-web-portal/utils/formatDeviceName";
import {
  GatewaySeenDevice,
  useGetDevice,
  useGetGatewaySeenDevices,
} from "@tether-web-portal/api/devices/hooks";
import { useGetPropertyDetail } from "@tether-web-portal/api/properties/hooks";
import { formatCircuitPhaseTitle } from "../circuits/formatCircuitPhaseTitle";
import { DeviceIcon } from "../../device/DeviceIcon";
import { RouterLink } from "@tether-web-portal/components/common";

export const GatewayLinkedDevices = ({
  device,
  installationId,
}: {
  device: DeviceWithLastSample;
  installationId: string;
}) => {
  const { data, isLoading, refetch, error } = useGetGatewaySeenDevices(device.id);
  const [isSearching, setIsSearching] = useState(false);
  const [lastCheck, setLastCheck] = useState<dayjs.Dayjs>(dayjs());

  let timeout: NodeJS.Timeout;

  useEffect(() => {
    (async () => {
      try {
        if (timeout) clearTimeout(timeout);

        if (!isSearching) return;

        // eslint-disable-next-line react-hooks/exhaustive-deps
        timeout = setTimeout(() => {
          if (!refetch) return;
          const now = dayjs();
          setLastCheck(now);
          refetch();
        }, 10000);

        return () => {
          clearTimeout(timeout);
        };
      } catch (error) {
        console.error("Could not fetch power", error);
      }
    })();
  }, [isSearching, refetch, lastCheck]);

  const numberLinked = data?.length + " " || "";

  return (
    <Box w="100%">
      <Text ml={4} fontSize="lg" fontWeight="bold">
        {isLoading ? "Loading..." : numberLinked + "Linked Devices"}
      </Text>

      {data?.map((device) => (
        <GatewayLinkedDevice installationId={installationId} key={device.deviceId} seenDevice={device} />
      ))}

      <Box>
        {isSearching && (
          <Text fontSize="lg" fontWeight="bold">
            Searching...
          </Text>
        )}

        {Boolean(error) && (
          <Alert status="error">
            <AlertIcon />
            {(error as Error).message}
          </Alert>
        )}

        <Button
          p={3}
          variant="outline"
          onClick={() => {
            setIsSearching(!isSearching);
            if (refetch) refetch();
          }}
        >
          {isSearching ? "Stop Searching" : "Search for devices"}
        </Button>

        {Boolean(lastCheck) && <Text mt={2}>Last checked for devices {lastCheck.format("HH:mm:ss")}</Text>}
      </Box>
    </Box>
  );
};

interface RoomInfo {
  roomId: string | undefined;
  roomName: string | undefined;
}

const GatewayLinkedDevice = ({
  seenDevice,
  installationId,
}: {
  seenDevice: GatewaySeenDevice;
  installationId: string;
}) => {
  const { data, error } = useGetDevice(seenDevice.deviceId);
  const { propertyInfo } = useGetPropertyDetail(installationId);

  function findRoomIdByDeviceId(
    deviceId: string | undefined,
    propertyInfo: PropertyDetail | undefined
  ): RoomInfo {
    const idToMatch = deviceId;

    if (!idToMatch || !propertyInfo?.rooms) {
      return { roomId: undefined, roomName: undefined };
    }
    const matchingRoom = propertyInfo.rooms.find((room) => {
      const foundRl = room.roomLocations?.find((roomLocation) => roomLocation?.device?.id === idToMatch);
      if (foundRl) {
        return true;
      }
      return false;
    });

    if (matchingRoom) {
      return { roomId: matchingRoom.id, roomName: matchingRoom.name };
    }

    return { roomId: undefined, roomName: undefined };
  }
  const matchingRoomInfo = findRoomIdByDeviceId(data?.id, propertyInfo);

  const id = data && data?.id;

  const lastCheckin = seenDevice.lastSeenTimestamp ? dayjs(seenDevice.lastSeenTimestamp).fromNow() : "Never";
  let title = data ? formatDeviceName(data.type) : "Loading...";
  if (error) title = "Could not get device information";

  const circuit = propertyInfo?.circuits.find((circuit) => circuit?.device?.id === id);

  let urlToOpen: string | undefined = undefined;
  if (matchingRoomInfo.roomId) {
    urlToOpen = "/area/room/" + matchingRoomInfo.roomId;
  }

  if (circuit) {
    urlToOpen = "/circuitnode/" + circuit.circuitNodeId;
  }

  return (
    <Box
      // h={"268px"}
      height="100%"
      shadow="md"
      bg="white"
      borderRadius={8}
      borderWidth={1}
      borderColor="gray.50"
      display="flex"
      flexDirection="column"
      p={4}
      mb={2}
    >
      <Box d="flex" justifyContent="space-between" alignItems="left">
        <Box flexDirection="row" justifyContent="space-between" alignItems="center">
          {Boolean(matchingRoomInfo?.roomName) && (
            <Text fontWeight={500} fontSize={18}>
              {matchingRoomInfo.roomName}
            </Text>
          )}
          {circuit && (
            <Text fontWeight={500} fontSize={15}>
              {formatCircuitPhaseTitle(circuit, true)}
            </Text>
          )}
        </Box>

        {Boolean(urlToOpen) && (
          <RouterLink to={urlToOpen!} textDecoration={"underline"} style={{ border: "1px solid #333" }}>
            Open Device
          </RouterLink>
        )}
      </Box>

      <Box d="flex">
        <Box mr={2}>
          <DeviceIcon type={data?.type} />
        </Box>
        <Box d="flex" flexDirection="column">
          <Text>{title}</Text>
          {Boolean(data?.serialNumber) && <Text>Serial: {data?.serialNumber}</Text>}
          {Boolean(data?.sku) && <Text>SKU: {data?.sku}</Text>}
        </Box>
      </Box>

      <Box mt={2}>
        <Text>Last Seen: {lastCheckin}</Text>
      </Box>
    </Box>
  );
};
